import React, { useState, useEffect } from 'react';
import { HINDI_RECIPES, STATE_HINDI_MAP, CUISINE_HINDI_MAP } from '../hindi-recipes';
import { TAMIL_RECIPES, STATE_TAMIL_MAP, CUISINE_TAMIL_MAP } from '../tamil-recipes';
import { TELUGU_RECIPES, STATE_TELUGU_MAP, CUISINE_TELUGU_MAP } from '../telugu-recipes';
import { ARABIC_RECIPES, STATE_ARABIC_MAP, CUISINE_ARABIC_MAP } from '../arabic-recipes';
import { getRecipeImage } from '../recipe-images';
import { getIngredientImageUrl } from '../ingredient-images';
import {
  getCoreDishTitle,
  getStateName,
  getStateStyleLabel,
  getStateIcon
} from '../recipe-variants';

export default function RecipeModal({
  recipe: initialRecipe,
  variants: initialVariants = [],
  initialHeadcount = 4,
  lang,
  setLang,
  t,
  onClose,
  onOpenInBot
}) {
  const [recipe, setRecipe] = useState(initialRecipe);
  const [variants, setVariants] = useState(initialVariants || []);
  const [headcount, setHeadcount] = useState(initialHeadcount);
  const [activeTab, setActiveTab] = useState('summary');
  const [timerSeconds, setTimerSeconds] = useState(600);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const isEng = lang === 'English';
  const isTa = lang === 'Tamil';
  const isHi = lang === 'Hindi';
  const isTe = lang === 'Telugu';
  const isKn = lang === 'Kannada';
  const isMl = lang === 'Malayalam';
  const isAr = lang === 'Arabic';

  useEffect(() => {
    setRecipe(initialRecipe);
    setHeadcount(initialHeadcount);
    if (initialVariants && initialVariants.length > 0) {
      setVariants(initialVariants);
    } else if (initialRecipe?.core_dish) {
      fetch('/api/recipes')
        .then(res => res.json())
        .then(data => {
          const matches = (data.recipes || []).filter(r => r.core_dish === initialRecipe.core_dish);
          if (matches.length > 0) setVariants(matches);
        })
        .catch(console.error);
    }
  }, [initialHeadcount, initialRecipe, initialVariants]);

  const handleSwitchVariant = (variantId) => {
    const chosen = variants.find(v => v.id === variantId);
    if (chosen) {
      setRecipe(chosen);
    }
  };

  useEffect(() => {
    let interval = null;
    if (isTimerRunning && timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds(s => s - 1);
      }, 1000);
    } else if (timerSeconds === 0 && isTimerRunning) {
      setIsTimerRunning(false);
      let alertMsg = '⏰ Cooking Timer Finished! Time to check your dish!';
      if (isTa) alertMsg = '⏰ சமையல் டைமர் முடிந்தது! உங்கள் உணவை சரிபார்க்கவும்!';
      else if (isHi) alertMsg = '⏰ कुकिंग टाइमर समाप्त! अपने व्यंजन की जांच करें!';
      else if (isTe) alertMsg = '⏰ కుకింగ్ టైమర్ ముగిసింది! మీ వంటకాన్ని తనిఖీ చేయండి!';
      else if (isKn) alertMsg = '⏰ ಅಡುಗೆ ಟೈಮರ್ ಮುಗಿದಿದೆ! ನಿಮ್ಮ ಅಡುಗೆಯನ್ನು ಪರಿಶೀಲಿಸಿ!';
      else if (isMl) alertMsg = '⏰ പാചക ടೈമർ പൂർത്തിയായി! നിങ്ങളുടെ വിഭവം പരിശോധിക്കുക!';
      else if (isAr) alertMsg = '⏰ انتهى مؤقت الطهي! حان وقت تفقد طبقك الشهي!';
      alert(alertMsg);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timerSeconds, isEng, isTa, isHi, isTe, isKn, isMl, isAr]);

  if (!recipe) return null;

  const recId = String(recipe.id || '');
  const factor = headcount / 4;

  const scaleQuantity = (qtyStr, mult) => {
    if (!qtyStr || mult === 1) return qtyStr;
    const match = qtyStr.match(/^([\d\.\/\s]+)\s*(.*)$/);
    if (!match) return qtyStr;

    let numPart = match[1].trim();
    let unitPart = match[2];
    let val = 0;
    if (numPart.includes('/')) {
      const parts = numPart.split('/');
      val = parseFloat(parts[0]) / parseFloat(parts[1]);
    } else {
      val = parseFloat(numPart);
    }
    if (isNaN(val)) return qtyStr;

    const scaled = val * mult;
    let formatted = scaled;
    if (scaled === 0.25) formatted = "1/4";
    else if (scaled === 0.5) formatted = "1/2";
    else if (scaled === 0.75) formatted = "3/4";
    else if (scaled === 1.5) formatted = "1.5";
    else if (scaled === 2.5) formatted = "2.5";
    else formatted = Number.isInteger(scaled) ? scaled : scaled.toFixed(1);

    return `${formatted} ${unitPart}`.trim();
  };

  const formatTimer = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
  };

  const getRecipeTitle = () => {
    if (isTa) return TAMIL_RECIPES[recId]?.meal_ta || TAMIL_RECIPES[recipe.id]?.meal_ta || recipe.meal_ta || recipe.meal;
    if (isHi) return HINDI_RECIPES[recId]?.meal_hi || HINDI_RECIPES[recipe.id]?.meal_hi || recipe.meal_hi || recipe.meal;
    if (isTe) return TELUGU_RECIPES[recId]?.meal_te || TELUGU_RECIPES[recipe.id]?.meal_te || recipe.meal;
    if (isAr) return ARABIC_RECIPES[recId]?.meal_ar || ARABIC_RECIPES[recipe.id]?.meal_ar || recipe.meal;
    return recipe.meal;
  };

  const getRecipeDesc = () => {
    if (isTa) return TAMIL_RECIPES[recId]?.desc_ta || TAMIL_RECIPES[recipe.id]?.desc_ta || recipe.desc_ta || recipe.description;
    if (isHi) return HINDI_RECIPES[recId]?.desc_hi || HINDI_RECIPES[recipe.id]?.desc_hi || recipe.desc_hi || recipe.description;
    if (isTe) return TELUGU_RECIPES[recId]?.desc_te || TELUGU_RECIPES[recipe.id]?.desc_te || recipe.description;
    if (isAr) return ARABIC_RECIPES[recId]?.desc_ar || ARABIC_RECIPES[recipe.id]?.desc_ar || recipe.description;
    return recipe.description;
  };

const STATE_KANNADA_MAP = {
  "Tamil Nadu": "ತಮಿಳುನಾಡು",
  "Kerala": "ಕೇರಳ",
  "Karnataka": "ಕರ್ನಾಟಕ",
  "Andhra Pradesh": "ಆಂಧ್ರಪ್ರದೇಶ",
  "Telangana": "ತೆಲಂಗಾಣ"
};

const STATE_MALAYALAM_MAP = {
  "Tamil Nadu": "തമിഴ്‌നാട്",
  "Kerala": "കേരളം",
  "Karnataka": "ಕರ್ണാടക",
  "Andhra Pradesh": "ആന്ധ്രാപ്രദേശ്",
  "Telangana": "തെലങ്കാന"
};

  const getRecipeState = () => {
    if (isTa) return TAMIL_RECIPES[recId]?.state_ta || STATE_TAMIL_MAP[recipe.state] || recipe.state;
    if (isHi) return HINDI_RECIPES[recId]?.state_hi || STATE_HINDI_MAP[recipe.state] || recipe.state;
    if (isTe) return TELUGU_RECIPES[recId]?.state_te || STATE_TELUGU_MAP[recipe.state] || recipe.state;
    if (isKn) return STATE_KANNADA_MAP[recipe.state] || recipe.state;
    if (isMl) return STATE_MALAYALAM_MAP[recipe.state] || recipe.state;
    if (isAr) return ARABIC_RECIPES[recId]?.state_ar || STATE_ARABIC_MAP[recipe.state] || recipe.state;
    return recipe.state;
  };

  const getRecipeCuisine = () => {
    if (isTa) return CUISINE_TAMIL_MAP[recipe.cuisine] || recipe.cuisine;
    if (isHi) return CUISINE_HINDI_MAP[recipe.cuisine] || recipe.cuisine;
    if (isTe) return CUISINE_TELUGU_MAP[recipe.cuisine] || recipe.cuisine;
    if (isAr) return CUISINE_ARABIC_MAP[recipe.cuisine] || recipe.cuisine;
    return recipe.cuisine;
  };

  const getDishesList = () => {
    if (isTe) {
      const teData = TELUGU_RECIPES[recId]?.dishes_te || TELUGU_RECIPES[recipe.id]?.dishes_te;
      if (teData && teData.length > 0) {
        return teData.map(d => ({
          name: d.name_te,
          ingredients: d.ingredients_te,
          quick: d.quick_te
        }));
      }
    }
    if (isAr) {
      const arData = ARABIC_RECIPES[recId]?.dishes_ar || ARABIC_RECIPES[recipe.id]?.dishes_ar;
      if (arData && arData.length > 0) {
        return arData.map(d => ({
          name: d.name_ar,
          ingredients: d.ingredients_ar,
          quick: d.quick_ar
        }));
      }
    }
    if (isHi) {
      const hData = HINDI_RECIPES[recId]?.dishes_hi || HINDI_RECIPES[recipe.id]?.dishes_hi;
      if (hData && hData.length > 0) {
        return hData.map(d => ({
          name: d.name_hi,
          ingredients: d.ingredients_hi,
          quick: d.quick_hi
        }));
      }
    }
    if (isTa) {
      const tData = TAMIL_RECIPES[recId]?.dishes_ta || TAMIL_RECIPES[recipe.id]?.dishes_ta;
      if (tData && tData.length > 0) {
        return tData.map(d => ({
          name: d.name_ta,
          ingredients: d.ingredients_ta,
          quick: d.quick_ta
        }));
      }
    }
    return (recipe.dishes || []).map(d => ({
      name: d.name,
      ingredients: d.ingredients,
      quick: d.quick
    }));
  };

  const copyRecipe = () => {
    const mealTitle = getRecipeTitle();
    const desc = getRecipeDesc();
    const dishes = getDishesList();
    
    let text = `🍲 ${mealTitle} (${headcount} ${t.modalPeople})\n\n`;
    text += `⏱ ${t.modalCookingTime} ${recipe.minutes} ${t.cardMins} | 📍 ${t.modalRegion} ${getRecipeState()}\n`;
    text += `${desc}\n\n`;

    dishes.forEach((d, idx) => {
      text += `--- ${d.name} ---\n`;
      text += `${t.modalReqIngredients.replace('{headcount}', headcount)}:\n`;
      d.ingredients.forEach(([name, qty]) => {
        text += `• ${scaleQuantity(qty, factor)} ${name}\n`;
      });
      text += `\n${t.modalQuickMethod}\n`;
      const steps = d.quick.split('|');
      steps.forEach((s, i) => {
        text += `${i + 1}. ${s.trim()}\n`;
      });
      text += `\n`;
    });

    if (isEng) text += `MyMenuBuddy Smart Food AI`;
    else if (isTa) text += `மைமெனுபடி (MyMenuBuddy) உணவு AI`;
    else if (isHi) text += `मायमेनुबडी (MyMenuBuddy) फ़ूड AI`;
    else if (isTe) text += `మైమెనూబడ్డీ (MyMenuBuddy) ఫుడ్ AI`;
    else if (isAr) text += `ماي منيو بودي (MyMenuBuddy) الذكاء الاصطناعي للطبخ`;

    navigator.clipboard?.writeText(text);
    alert(t.modalCopyAlert);
  };

  const title = getRecipeTitle();
  const dishes = getDishesList();
  const heroImage = getRecipeImage(recipe);

  return (
    <div className="modal active">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-container">
        {/* Visual Recipe Hero Banner */}
        <div className="modal-hero-banner">
          <img
            src={heroImage}
            alt={title}
            className="modal-hero-img"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `/images/recipes/recipe_${recipe?.id || 1}.jpg`;
            }}
          />
          <div className="modal-hero-overlay"></div>

          {/* Quick In-Modal Language Switcher */}
          {setLang && (
            <div className="modal-lang-toggle-bar">
              <span className="modal-lang-icon">🌐</span>
              <div className="modal-lang-buttons">
                <button
                  type="button"
                  className={`modal-lang-btn ${isEng ? 'active' : ''}`}
                  onClick={() => setLang('English')}
                  title="Switch recipe to English"
                >
                  EN
                </button>
                <button
                  type="button"
                  className={`modal-lang-btn ${isTa ? 'active' : ''}`}
                  onClick={() => setLang('Tamil')}
                  title="செய்முறையை தமிழில் பார்க்க"
                >
                  தமிழ்
                </button>
                <button
                  type="button"
                  className={`modal-lang-btn ${isHi ? 'active' : ''}`}
                  onClick={() => setLang('Hindi')}
                  title="रेसिपी हिंदी में देखें"
                >
                  हिंदी
                </button>
                <button
                  type="button"
                  className={`modal-lang-btn ${isTe ? 'active' : ''}`}
                  onClick={() => setLang('Telugu')}
                  title="రెసిపీని తెలుగులో చూడండి"
                >
                  తెలుగు
                </button>
                <button
                  type="button"
                  className={`modal-lang-btn ${isKn ? 'active' : ''}`}
                  onClick={() => setLang('Kannada')}
                  title="ಪಾಕವಿಧಾನವನ್ನು ಕನ್ನಡದಲ್ಲಿ ವೀಕ್ಷಿಸಿ"
                >
                  ಕನ್ನಡ
                </button>
                <button
                  type="button"
                  className={`modal-lang-btn ${isMl ? 'active' : ''}`}
                  onClick={() => setLang('Malayalam')}
                  title="പാചകക്കുറിപ്പ് മലയാളത്തിൽ കാണുക"
                >
                  മലയാളം
                </button>
                <button
                  type="button"
                  className={`modal-lang-btn ${isAr ? 'active' : ''}`}
                  onClick={() => setLang('Arabic')}
                  title="عرض الوصفة باللغة العربية"
                >
                  العربية
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            className="modal-close-btn modal-hero-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
          <div className="modal-hero-content">
            <h3>{title}</h3>
            <div className="card-badges">
              <span className={`badge ${recipe.diet === 'Vegetarian' ? 'badge-veg' : 'badge-nonveg'}`}>
                {recipe.diet === 'Vegetarian' ? t.cardVegBadge : t.cardNonVegBadge}
              </span>
              <span className="badge badge-timing">⏱ {recipe.minutes} {t.cardMins}</span>
              <span className="badge badge-cuisine">📍 {getRecipeCuisine()} ({getRecipeState()})</span>
              <span className="badge badge-timing">👥 {headcount} {t.modalPeople}</span>
            </div>

            {/* Combination Dishes Breakdown */}
            <div className="modal-combo-pills-row">
              <span className="modal-combo-tag">
                {isTa ? '🍱 கூட்டு உணவுகள்:' : (isHi ? '🍱 कॉम्बो थाली:' : (isTe ? '🍱 కాంబో వంటకాలు:' : (isKn ? '🍱 ಕಾಂಬೋ ತಿನಿಸುಗಳು:' : (isMl ? '🍱 കോംബോ വിഭവങ്ങൾ:' : (isAr ? '🍱 أطباق الوجبة:' : '🍱 Combo Dishes:')))))}
              </span>
              <div className="modal-combo-chips">
                {dishes.map((d, idx) => (
                  <span key={idx} className="modal-combo-chip">
                    {d.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-body">
          {/* Regional Cooking Style Selector (Dropdown + Quick Chips) */}
          {variants && variants.length > 0 && (
            <div className="modal-style-box">
              <div className="modal-style-header">
                <div className="modal-style-title-group">
                  <span className="modal-style-icon">📍</span>
                  <span className="modal-style-title">
                    {isTa ? 'பிராந்திய சமையல் முறை தேர்வு:' : 
                     isHi ? 'क्षेत्रीय पाक शैली चुनें:' : 
                     isTe ? 'ప్రాంతీయ వంట శైలిని ఎంచుకోండి:' : 
                     isKn ? 'ಪ್ರಾದೇಶಿಕ ಅಡುಗೆ ಶೈಲಿ ಆಯ್ಕೆಮಾಡಿ:' : 
                     isMl ? 'പാചക ശൈലി തിരഞ്ഞെടുക്കുക:' : 
                     isAr ? 'اختر النمط الإقليمي للطهي:' : 
                     'Select Regional Cooking Style:'}
                  </span>
                </div>
                <span className="modal-style-badge">
                  {variants.length} {isTa ? 'மாநில முறைகள்' : 'Regional Styles'}
                </span>
              </div>

              {/* Native Dropdown Selector */}
              <div className="modal-style-select-wrap">
                <select
                  className="modal-style-select"
                  value={recipe.id}
                  onChange={(e) => handleSwitchVariant(Number(e.target.value))}
                  aria-label="Select state cooking style"
                >
                  {variants.map(v => (
                    <option key={v.id} value={v.id}>
                      {getStateIcon(v.state)} {getStateStyleLabel(v.state, lang)} — {v.state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quick One-Tap State Chips */}
              {variants.length > 1 && (
                <div className="modal-style-chips">
                  {variants.map(v => {
                    const isCurrent = v.id === recipe.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        className={`modal-style-chip ${isCurrent ? 'active' : ''}`}
                        onClick={() => handleSwitchVariant(v.id)}
                      >
                        <span className="style-chip-icon">{getStateIcon(v.state)}</span>
                        <span className="style-chip-name">{getStateName(v.state, lang)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Dynamic Portion Scaler */}
          <div className="modal-portion-box">
            <label className="portion-label">{t.modalPortionLabel}</label>
            <div className="portion-btn-group">
              <button
                type="button"
                className={`portion-btn ${headcount === 2 ? 'active' : ''}`}
                onClick={() => setHeadcount(2)}
              >
                {t.modalPortion1to2}
              </button>
              <button
                type="button"
                className={`portion-btn ${headcount === 4 ? 'active' : ''}`}
                onClick={() => setHeadcount(4)}
              >
                {t.modalPortion3to4}
              </button>
              <button
                type="button"
                className={`portion-btn ${headcount === 6 ? 'active' : ''}`}
                onClick={() => setHeadcount(6)}
              >
                {t.modalPortion5to6}
              </button>
              <button
                type="button"
                className={`portion-btn ${headcount === 8 ? 'active' : ''}`}
                onClick={() => setHeadcount(8)}
              >
                {t.modalPortion8plus}
              </button>
            </div>
          </div>

          {/* Mode Tabs */}
          <div className="modal-tabs">
            <button
              type="button"
              className={`modal-tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              {t.modalTabSummary}
            </button>
            <button
              type="button"
              className={`modal-tab-btn ${activeTab === 'masterclass' ? 'active' : ''}`}
              onClick={() => setActiveTab('masterclass')}
            >
              {t.modalTabMasterclass}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'summary' ? (
            <div className="modal-dishes-list">
              {dishes.map((d, idx) => {
                const steps = d.quick.split('|');

                return (
                  <div key={idx} className="modal-dish-card">
                    <h4>{t.modalPart} {idx + 1}: {d.name}</h4>
                    <div className="modal-two-col">
                      <div className="modal-ing-column">
                        <h5>{t.modalReqIngredients.replace('{headcount}', headcount)}</h5>
                        
                        {/* Visual Ingredient Cards with Images */}
                        <div className="modal-ing-visual-grid">
                          {d.ingredients.map(([name, qty], iIdx) => {
                            const ingImg = getIngredientImageUrl(name);
                            return (
                              <div key={iIdx} className="modal-ing-card">
                                <div className="modal-ing-img-wrap">
                                  <img
                                    src={ingImg}
                                    alt={name}
                                    className="modal-ing-img"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "/images/ingredient/Salt.png";
                                    }}
                                  />
                                </div>
                                <div className="modal-ing-details">
                                  <span className="modal-ing-qty">{scaleQuantity(qty, factor)}</span>
                                  <span className="modal-ing-name">{name}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="modal-steps-column">
                        <h5>{t.modalQuickMethod}</h5>
                        <ol className="modal-quick-steps">
                          {steps.map((s, sIdx) => (
                            <li key={sIdx}>{s.trim()}</li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="modal-guided-view">
              <div className="guided-header-banner">
                <p dangerouslySetInnerHTML={{ __html: t.modalMasterclassBanner }} />
              </div>
              {dishes.map((d, idx) => {
                const steps = d.quick.split('|');
                return (
                  <div key={idx} className="guided-dish-block">
                    <h4>{d.name}:</h4>
                    <div className="guided-steps-interactive">
                      {steps.map((st, sIdx) => (
                        <label key={sIdx} className="guided-step-item">
                          <input type="checkbox" className="step-checkbox" />
                          <span className="step-custom-box"></span>
                          <div className="step-text-wrap">
                            <span className="step-index">{t.modalStep} {sIdx + 1}</span>
                            <p className="step-instruction">{st.trim()}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="modal-timer-box">
            <span>{t.modalTimerLabel}</span>
            <div className="timer-digits">{formatTimer(timerSeconds)}</div>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => setIsTimerRunning(!isTimerRunning)}
            >
              {isTimerRunning ? t.modalTimerPause : t.modalTimerStart}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => {
                setIsTimerRunning(false);
                setTimerSeconds(600);
              }}
            >
              {t.modalTimerReset}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={copyRecipe}>
              {t.modalBtnCopy}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                onClose();
                onOpenInBot(recipe, headcount);
              }}
            >
              {t.modalBtnAskAI}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
